import { useCallback, useEffect, useRef } from 'react';
import { type OrderRequest, PositionStatusReasonUnspecified } from '@/api/spec.ts';
import { ORDER_CONFIRMING_NOTIFICATION_DELAY } from '@/common/timeout_constants';
import { usePositionStore } from '@/feature/terminal_tables/position/PositionStore.ts';
import { getPositionErrorInfo } from '@/feature/terminal_tables/position/positionNotificationMessages.ts';
import { useTradingWebSocketSender } from '../../../hooks/ws/useTradingWebSocketSender.ts';
import useAppStore from '../../../store/rootStore.ts';

import { buildApiAddMarginOrder } from '../utils/apiOrderParams.ts';
import { getMarketDataById } from '../utils/helpers.ts';
import { ApiStatusToTransactionStatus } from '../utils/statusMappers.ts';

export function usePositionAPI() {
  const accountId = useAppStore((state) => state.account?.id);
  const { sendMessage } = useTradingWebSocketSender();
  const { posting, setPostingPreparing, setPostingConfirming, resetPosting, setPostingFailed } = usePositionStore();

  const confirmingTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const resetProcess = useCallback(() => {
    clearTimeout(confirmingTimeoutRef.current);
    confirmingTimeoutRef.current = undefined;
    resetPosting();
  }, [resetPosting]);

  const addMargin = useCallback(
    (marginAmount: bigint, marketId: number) => {
      const marketData = getMarketDataById(marketId);
      if (!accountId || !marketData?.id) {
        const context = 'Account ID or market not available for margin addition';
        const errorInfo = getPositionErrorInfo(PositionStatusReasonUnspecified, context);
        console.error(errorInfo.message);
        setPostingFailed(PositionStatusReasonUnspecified, context);
        return;
      }

      const requestId = useAppStore.getState().generateRequestId();
      setPostingPreparing(marketId, requestId);

      try {
        const marginRequest = buildApiAddMarginOrder(marginAmount, marketId, accountId, requestId);

        if (sendMessage<OrderRequest>(marginRequest)) {
          clearTimeout(confirmingTimeoutRef.current);
          // Delay showing notification to avoid flashing on fast confirmations
          confirmingTimeoutRef.current = setTimeout(() => {
            setPostingConfirming();
            confirmingTimeoutRef.current = undefined;
          }, ORDER_CONFIRMING_NOTIFICATION_DELAY);
        } else {
          const context = 'Margin request WebSocket send failed';
          const errorInfo = getPositionErrorInfo(PositionStatusReasonUnspecified, context);
          console.error(errorInfo.message);
          setPostingFailed(PositionStatusReasonUnspecified, context);
        }
      } catch (error) {
        const context = error instanceof Error ? error.message : 'Failed to build margin request';
        const errorInfo = getPositionErrorInfo(PositionStatusReasonUnspecified, context);
        console.error('Failed to build margin request.', error);
        setPostingFailed(PositionStatusReasonUnspecified, context);
      }
    },
    [accountId, sendMessage, setPostingPreparing, setPostingFailed]
  );

  // Clear confirming timeout when status changes or on unmount
  useEffect(() => {
    return () => {
      clearTimeout(confirmingTimeoutRef.current);
      confirmingTimeoutRef.current = undefined;
    };
  }, [posting.status]);

  const currentStatus = ApiStatusToTransactionStatus[posting.status] || 'idle';

  const isPending = currentStatus === 'preparing' || currentStatus === 'confirming';
  const isSuccess = currentStatus === 'confirmed';

  return {
    addMargin,
    resetProcess,
    currentStatus,
    isPending,
    isSuccess,
    isError: currentStatus === 'failed',
  };
}
